import type { IAgentTool } from '../IAgentTool';
import type { Node, Edge } from '../../../models/scene';
import type { RdmMeta } from '../../../../domains/rdm/models';

const DIAGRAM_TYPE_MAP: Record<string, string> = {
  orgchart: 'orgchart',
  'org-chart': 'orgchart',
  genogram: 'genogram',
  process: 'process',
  flowchart: 'process',
  bpmn: 'process',
  network: 'network',
  table: 'table',
  generic: 'generic',
};

/**
 * RDM Context Tool
 *
 * Generates an RDM text representation of the current diagram.
 * Supports two modes:
 *   - "summary" (default): lightweight stats (~200 tokens) — use when you just
 *     need to know what's on the canvas before deciding what to do.
 *   - "full": bounded RDM markup — use when you need the structure to
 *     plan edits or answer detailed questions.
 */
export const rdmContextTool: IAgentTool = {
  name: 'rdm_context',
  description:
    'Get a structured representation of the current diagram. ' +
    'Use mode="summary" (~200 tokens) to quickly check node/edge counts, types, and top-level structure. ' +
    'Use mode="full" for bounded RDM markup; check truncated and total counts before interpreting omitted content. ' +
    'Default is "summary" — prefer it to save context window.',
  parameters: {
    type: 'object',
    properties: {
      mode: {
        type: 'string',
        enum: ['summary', 'full'],
        description: 'Output mode: "summary" for lightweight stats, "full" for bounded RDM markup (default: "summary")',
      },
      maxNodes: {
        type: 'number',
        description: 'Maximum number of nodes to include in full mode (default: 200)',
      },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params, context) {
    const mode = (params.mode as string) || 'summary';
    const rdmType = DIAGRAM_TYPE_MAP[context.diagramType] || 'generic';
    const allNodes = context.diagramData.nodes;
    const allEdges = context.diagramData.edges;

    if (mode === 'summary') {
      return buildSummary(allNodes, allEdges, rdmType);
    }

    if (mode !== 'full') {
      return { success: false, summary: 'Use mode="summary" or mode="full".' };
    }
    const requestedLimit = params.maxNodes ?? 200;
    if (typeof requestedLimit !== 'number' || !Number.isInteger(requestedLimit) || requestedLimit < 1) {
      return { success: false, summary: 'maxNodes must be a positive integer.' };
    }
    const maxNodes = Math.min(requestedLimit, 500);
    const nodes = allNodes.slice(0, maxNodes);
    const nodeIds = new Set(nodes.map((node) => node.id));
    const edges = allEdges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
    const truncated = nodes.length < allNodes.length || edges.length < allEdges.length;

    const meta: Partial<RdmMeta> = {
      type: rdmType as RdmMeta['type'],
    };

    try {
      const { rdmService } = await import('../../../../domains/rdm/runtime');
      const rdmText = rdmService.fromRuntimeModel(nodes, edges, meta);

      return {
        success: true,
        summary: `Generated RDM representation: ${nodes.length} nodes, ${edges.length} edges.${truncated ? " Partial context; omitted content is not absent from the diagram." : ""}`,
        data: {
          rdm: rdmText,
          nodeCount: nodes.length,
          edgeCount: edges.length,
          totalNodeCount: allNodes.length,
          totalEdgeCount: allEdges.length,
          nodeLimit: maxNodes,
          truncated,
          scope: 'diagram-context',
        },
      };
    } catch (error) {
      return {
        success: false,
        summary: `Failed to generate RDM: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  },
};

function buildSummary(
  nodes: Node[],
  edges: Edge[],
  diagramType: string
) {
  // Count node types
  const typeCounts: Record<string, number> = {};
  for (const n of nodes) {
    const type = String(n.attrs?.shapeId ?? n.type ?? 'unknown');
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  }

  const targetIds = new Set(edges.map((edge) => edge.to));
  const rootCount = nodes.filter((node) => !targetIds.has(node.id)).length;

  const typeBreakdown = Object.entries(typeCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => `${type}: ${count}`)
    .join(', ');

  return {
    success: true,
    summary: `Diagram summary: ${diagramType} with ${nodes.length} nodes, ${edges.length} edges.`,
    data: {
      diagramType,
      nodeCount: nodes.length,
      edgeCount: edges.length,
      rootNodes: rootCount,
      nodeTypes: typeCounts,
      typeBreakdown,
    },
  };
}
